import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiGet } from '../composables/apiGet'
import { apiMutate } from '../composables/apiMutate'

export interface Reminder {
  id: number
  message: string
  fire_at: string
  fired_at: string | null
  done: boolean
  reminder_schedule_id: number | null
  created_at: string
}

export interface ReminderSchedule {
  id: number
  message: string
  recurrence_type: 'daily' | 'weekly' | 'monthly' | 'yearly'
  fire_at_time: string
  timezone: string
  ends_on: string | null
  next_fire_at: string | null
  day_of_week: number | null
  created_at: string
}

export interface ReminderScheduleInput {
  message: string
  recurrence_type: string
  fire_at_time: string
  timezone?: string
  ends_on?: string | null
  day_of_week?: number | null
}

export interface ReminderInput {
  message: string
  fire_at: string
}

export const useRemindersStore = defineStore('reminders', () => {
  const reminders = ref<Reminder[]>([])
  const schedules = ref<ReminderSchedule[]>([])
  const loadError = ref<string | null>(null)
  const loading = ref(false)

  async function fetchAll(reminderStatus: 'pending' | 'done' | 'all' = 'pending'): Promise<void> {
    loading.value = true
    loadError.value = null
    try {
      const remindersUrl =
        reminderStatus === 'all' ? '/api/v1/reminders' : `/api/v1/reminders?done=${reminderStatus === 'done'}`
      const [remindersRes, schedulesRes] = await Promise.all([
        apiGet<{ reminders: Reminder[] }>(remindersUrl, { failureLabel: 'Failed to load reminders' }),
        apiGet<{ reminder_schedules: ReminderSchedule[] }>('/api/v1/reminder_schedules', {
          failureLabel: 'Failed to load schedules',
        }),
      ])
      reminders.value = remindersRes.reminders
      schedules.value = schedulesRes.reminder_schedules
    } catch (e) {
      loadError.value = e instanceof Error ? e.message : 'Failed to load reminders'
    } finally {
      loading.value = false
    }
  }

  async function fetchReminders(reminderStatus: 'pending' | 'done' | 'all'): Promise<void> {
    const url = reminderStatus === 'all' ? '/api/v1/reminders' : `/api/v1/reminders?done=${reminderStatus === 'done'}`
    const res = await apiGet<{ reminders: Reminder[] }>(url, { failureLabel: 'Failed to load reminders' })
    reminders.value = res.reminders
  }

  async function createSchedule(input: ReminderScheduleInput): Promise<void> {
    const res = await apiMutate<ReminderSchedule>('/api/v1/reminder_schedules', {
      method: 'POST',
      body: { reminder_schedule: input },
      failureLabel: 'Failed to create schedule',
    })
    schedules.value.unshift(res)
  }

  async function updateSchedule(id: number, input: Partial<ReminderScheduleInput>): Promise<void> {
    const res = await apiMutate<ReminderSchedule>(`/api/v1/reminder_schedules/${id}`, {
      method: 'PATCH',
      body: { reminder_schedule: input },
      failureLabel: 'Failed to update schedule',
    })
    const idx = schedules.value.findIndex((s) => s.id === id)
    if (idx !== -1) schedules.value[idx] = res
  }

  async function deleteSchedule(id: number): Promise<void> {
    await apiMutate<void>(`/api/v1/reminder_schedules/${id}`, {
      method: 'DELETE',
      failureLabel: 'Failed to delete schedule',
    })
    schedules.value = schedules.value.filter((s) => s.id !== id)
  }

  async function createReminder(input: ReminderInput): Promise<void> {
    // datetime-local gives a naive string — convert via Date so the browser's local timezone is applied
    const fire_at = new Date(input.fire_at).toISOString()
    const res = await apiMutate<Reminder>('/api/v1/reminders', {
      method: 'POST',
      body: { reminder: { ...input, fire_at } },
      failureLabel: 'Failed to create reminder',
    })
    reminders.value.unshift(res)
  }

  async function updateReminder(id: number, input: Partial<ReminderInput>): Promise<void> {
    const payload: Partial<ReminderInput> = { ...input }
    if (payload.fire_at) {
      payload.fire_at = new Date(payload.fire_at).toISOString()
    }
    const res = await apiMutate<Reminder>(`/api/v1/reminders/${id}`, {
      method: 'PATCH',
      body: { reminder: payload },
      failureLabel: 'Failed to update reminder',
    })
    const idx = reminders.value.findIndex((r) => r.id === id)
    if (idx !== -1) reminders.value[idx] = res
  }

  async function toggleDone(id: number, done: boolean): Promise<void> {
    const idx = reminders.value.findIndex((r) => r.id === id)
    if (idx !== -1) reminders.value[idx] = { ...reminders.value[idx], done }
    try {
      const res = await apiMutate<Reminder>(`/api/v1/reminders/${id}`, {
        method: 'PATCH',
        body: { reminder: { done } },
        failureLabel: 'Failed to update reminder',
      })
      if (idx !== -1) reminders.value[idx] = res
    } catch (e) {
      await fetchAll()
      throw e
    }
  }

  async function deleteReminder(id: number): Promise<void> {
    await apiMutate<void>(`/api/v1/reminders/${id}`, {
      method: 'DELETE',
      failureLabel: 'Failed to delete reminder',
    })
    reminders.value = reminders.value.filter((r) => r.id !== id)
  }

  return {
    reminders,
    schedules,
    loadError,
    loading,
    fetchAll,
    fetchReminders,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    createReminder,
    updateReminder,
    toggleDone,
    deleteReminder,
  }
})
